// owner-map: OP-05a.spa
// owner-map: OP-04b.spa
/**
 * The realtime read-through — the client command half of the realtime channel.
 *
 * A realtime hint (`forecast.created` / `contract.resolved` over `GET /api/events`)
 * carries only a `qkey` (== `run.contractKey`), never a snapshot, and says only WHEN to
 * read. So every hint, whatever its type, does the same thing: `reconcile([qkey])` re-reads every
 * row the viewer can see on that contract and applies ALL of what a row can carry. A
 * reconnect does the same over every contract the tab has loaded (`reconcileLoaded`),
 * because the channel has no replay buffer and an event missed while it was down is
 * otherwise lost. Every read is lean: a run takes the pointers, verdict and premise from it; a
 * run whose detail is loaded is promoted from the history it already holds, and re-read by id
 * when the row shows a newer saved vintage or a finish (loadForecastDetail.ts refreshDetail); a lean
 * run is never promoted, and its newer forecast is read by id once its pointers land
 * (loadForecastDetail.ts hasNewerForecast).
 *
 * One read, one apply, for both populations. `GET /api/forecasts/by-contract` returns the
 * owner's rows and the rows shared with the viewer together; each row's apply resolves the
 * slice its run lives in (`runsById` or `sharedRunsById`) itself, so there is no per-type
 * or per-population arm to keep in step.
 *
 * Every apply is idempotent (delivery is at-least-once and unordered):
 * - `mergeRowReportPointers` — monotonic union through the field's single owner.
 * - `applyResolvedRow` — no-op once a terminal verdict is stamped.
 * - `applyPremiseRow` — takes the server's premise record; no-op when it is unchanged.
 * - `applyNewestReforecast` — the staleness guard makes an applied fire a no-op.
 *
 * A row for a run the tab has not loaded is a no-op in all three: loading owns what is in
 * the store, and this only brings loaded runs up to date.
 */

import { apiGet as defaultApiGet } from "../lib/apiClient.js";
import { applyNewestReforecast, mergeRowReportPointers } from "../lib/forecast/reforecastPickup.js";
import { nextPremiseRecord } from "../lib/forecast/branchPremise.js";
import { contractResolved, premiseCleared } from "../lib/forecast/reducer.js";
import { fetchForecastReport } from "../lib/forecast/reportPointer.js";
import { rowRunId } from "../lib/forecast/hydrate.js";
import { isSettled, isVoided } from "../lib/forecast/postResolutionRuns.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import { selectRun } from "../lib/forecast/selectors.js";
import { sharedLiveVintages } from "../lib/forecast/sharedLiveVintages.js";
import { BY_CONTRACT_MAX_QKEYS, forecastApi } from "../services/forecastApi.js";
import { markArrival } from "../store/arrivalsStore.js";
import { dispatchRun, getRunState, runStore } from "../store/runStore.js";
import { refreshDetail, savedVintageChanged } from "./loadForecastDetail.js";

type ApiGet = (url: string, options?: unknown) => Promise<{ ok?: boolean; body?: unknown }>;

interface ForecastRow {
  id?: string;
  forecastId?: string;
  contractKey?: string | null;
  contractStatus?: unknown;
  resolution?: unknown;
  [k: string]: unknown;
}

export interface ReconcileDeps {
  apiGet?: ApiGet;
  fetchReport?: typeof fetchForecastReport;
  cache?: { get: (k: string) => unknown; set: (k: string, v: unknown) => unknown };
  /** Re-reads runs whose loaded detail the server has since changed (default: refreshDetail). */
  refresh?: (runIds: string[]) => Promise<void>;
}

const defaultRefresh = (runIds: string[]) =>
  refreshDetail({ store: runStore, services: { api: forecastApi } } as any, runIds);

// Same key space and TTL convention as the on-load overlay's cache, but a separate Map: this
// fires per realtime event, so sharing is not worth coupling the modules.
const REPORT_TTL_MS = 5 * 60 * 1000;
const _pickupReportCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });

/**
 * Every row the viewer can read on any of `qkeys`, in the lean projection. A failed batch contributes nothing while
 * the other batches still land (best-effort, like every read-through here).
 */
async function fetchRowsByContract(qkeys: string[], apiGet: ApiGet): Promise<ForecastRow[]> {
  const rows: ForecastRow[] = [];
  for (let i = 0; i < qkeys.length; i += BY_CONTRACT_MAX_QKEYS) {
    const params = new URLSearchParams();
    for (const k of qkeys.slice(i, i + BY_CONTRACT_MAX_QKEYS)) params.append("qkey", k);
    params.set("view", "lean");
    const r = await apiGet(`/api/forecasts/by-contract?${params.toString()}`);
    if (!r || !r.ok || !r.body) continue;
    const body = r.body as { forecasts?: ForecastRow[] };
    if (Array.isArray(body.forecasts)) rows.push(...body.forecasts);
  }
  return rows;
}

/** The contracts the tab has loaded: each run's key, and a branch's premise key. */
function loadedQkeys(): string[] {
  const { runsById, sharedRunsById } = getRunState();
  const keys = new Set<string>();
  for (const slice of [runsById, sharedRunsById]) {
    for (const run of Object.values(slice ?? {})) {
      const loaded = run as
        | { contractKey?: unknown; forkEvent?: { contract_key?: unknown } | null }
        | undefined;
      const key = loaded?.contractKey;
      if (typeof key === "string" && key) keys.add(key);
      // A branch is also read on its premise's key: a derived branch may have no key of its
      // own, and its premise grading is hinted on the premise's key.
      const premiseKey = loaded?.forkEvent?.contract_key;
      if (typeof premiseKey === "string" && premiseKey) keys.add(premiseKey);
    }
  }
  return [...keys];
}

/**
 * Sets both `resolution` (drives the on-chart verdict visual) and `contractStatus` (gates the
 * resolution report section and the cadence pill), the pair a reload hydrates together.
 */
function applyResolvedRow(row: ForecastRow): boolean {
  const runId = rowRunId(row);
  if (!runId) return false;
  if (row.contractStatus !== "resolved") return false;
  const run = selectRun(getRunState(), runId);
  if (!run) return false;
  // A non-terminal resolution (in_flight/unresolved, e.g. from the resolver poll) must not
  // block stamping the terminal verdict, else the client stays stuck without the final verdict.
  // A void is final: a delayed resolved row, read before the void landed, cannot undo it.
  if (isVoided(run)) return false;
  if (isSettled(run)) {
    if (run.contractStatus === "resolved") return false;
    // The store already holds the final verdict (the read-through can land it before the
    // status); take only the status, so an older row's missing verdict cannot replace it.
    dispatchRun(contractResolved(runId, { contractStatus: "resolved" }));
    return true;
  }
  dispatchRun(
    contractResolved(runId, { contractStatus: "resolved", resolution: row.resolution ?? null }),
  );
  return true;
}

/**
 * Takes a branch's premise record (`fork.premise`) from its row. The server grades premises
 * and writes this record; it is the browser's only source for a premise's verdict, so a
 * graded branch's hint lands it here.
 *
 * Monotonic, since reads can land out of order: a terminal grade is never replaced by a
 * non-terminal one, and a CLEAR stamp the run already holds is kept when a read taken before
 * the clear arrives after it. Idempotent: an unchanged record is not re-applied.
 */
function applyPremiseRow(row: ForecastRow): boolean {
  const runId = rowRunId(row);
  if (!runId) return false;
  const run = selectRun(getRunState(), runId);
  if (!run) return false;
  const merged = nextPremiseRecord(
    run.premise,
    (row.fork as { premise?: unknown } | null | undefined)?.premise,
  );
  if (!merged) return false;
  dispatchRun(premiseCleared(runId, merged));
  return true;
}

function mayPromote(row: ForecastRow): boolean {
  if (sharedLiveVintages()) return true;
  const runId = rowRunId(row);
  return !!runId && !!getRunState().runsById?.[runId];
}

/**
 * Re-read every row on `qkeys`, lean, and bring each loaded run up to date. A lean row carries
 * the pointers, the verdict and the premise; a run whose detail is loaded is promoted from its
 * own history (the lean row's is empty, so its live moment would read as its creation), and a
 * lean run is never promoted (its newer forecast makes the detail loader read it by id). A run
 * whose detail is loaded but whose row shows a newer saved vintage, or that finished while the
 * channel was down, is re-read by id instead and promoted from that fresh row (refreshDetail);
 * a run this tab is executing is left to its own stream.
 *
 * Rows are reconciled CONCURRENTLY, so one slow or hung report read-through can't starve
 * the rest. The pointer merge and verdict stamp are synchronous and go first, so neither
 * waits on that row's vintage fetch.
 *
 * @returns `{ applied }` — verdicts stamped plus vintages promoted; a single row can
 *   contribute two. Pointer merges and refreshes are not counted.
 */
export async function reconcile(
  qkeys: string[],
  deps: ReconcileDeps = {},
): Promise<{ applied: number }> {
  const keys = [...new Set(qkeys.filter((k) => typeof k === "string" && k))];
  if (keys.length === 0) return { applied: 0 };
  const {
    apiGet = defaultApiGet as ApiGet,
    fetchReport = fetchForecastReport,
    cache = _pickupReportCache,
    refresh = defaultRefresh,
  } = deps;
  const rows = await fetchRowsByContract(keys, apiGet);
  // Decided against the runs as they are, before this read's rows touch them.
  const before = getRunState();
  const stale = new Set<string>();
  for (const row of rows) {
    const runId = rowRunId(row);
    if (runId && savedVintageChanged(selectRun(before, runId) ?? undefined, row as any)) {
      stale.add(runId);
    }
  }
  const refreshed = stale.size ? refresh([...stale]) : Promise.resolve();
  const results = await Promise.allSettled(
    rows.map(async (row) => {
      let n = 0;
      // First, and whatever the promote below decides: an on-demand completion's pointer is
      // not an async fire, so the promote bails on it — this is the only thing that lands it.
      mergeRowReportPointers(row);
      if (applyResolvedRow(row)) n += 1;
      if (applyPremiseRow(row)) n += 1;
      const runId = rowRunId(row);
      if (!runId || stale.has(runId) || !mayPromote(row)) return n;
      const run = selectRun(getRunState(), runId);
      if (!run || run.detailPending) return n;
      // Only pushed arrivals pulse as unseen timeline marks; the on-load overlay ignores the
      // returned moment.
      try {
        const held = { ...row, history: run.history, reportPointers: run.reportPointers };
        const arrivedMoment = await applyNewestReforecast(held as ForecastRow, {
          apiGet,
          cache,
          fetchReport,
        });
        if (arrivedMoment) {
          markArrival(arrivedMoment);
          n += 1;
        }
      } catch {
        // best-effort: a failed vintage read must not discard the verdict counted above
      }
      return n;
    }),
  );
  await refreshed;
  return { applied: results.reduce((sum, r) => sum + (r.status === "fulfilled" ? r.value : 0), 0) };
}

/** The reconnect catch-up over every contract the tab has loaded, lean (see `reconcile`). */
export async function reconcileLoaded(deps: ReconcileDeps = {}): Promise<{ applied: number }> {
  return reconcile(loadedQkeys(), deps);
}
